import ErrorBoundary from '@components/ErrorBoundary';
import { Spinner } from '@components/spinner';
import { PermissionClientAdminRead } from '@constants/Auth';
import { MainLayout, PublicLayout } from '@layouts';
import { Routes as RouteList } from '@models/route';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PrivateRoute, SignedOutPage } from '@swyft/auth';
import { MapProvider } from '@swyft/map';
import { FeatureNames, MainLayoutProvider } from '@swyft/ui';
import { useAppContext } from '@utils/appState';
import { useFeature } from '@utils/config';
import { flattenRoutes } from '@utils/routes';
import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { AnalyticRoutes } from './Analytic';
import { ContactRoutes } from './Contact';
import { ErrorRoutes } from './Error';
import { LeadRoutes } from './Lead';
import { ProductRoutes } from './Product';
import { QuestionnaireRoutes } from './Questionnaire';
import { PreviewRoutes } from './QuestionnairePreview';
import { QuoteRoutes } from './Quote';
import { SettingRoutes } from './Settings';

const publicRoutes: RouteList = flattenRoutes([...ErrorRoutes]);
const fullPageRoutes: RouteList = flattenRoutes([...PreviewRoutes]);

export const AppRoutes = (): React.ReactElement => {
  const { featureConfig: mapFeature } = useFeature(FeatureNames.Maps);
  const { enabled: contactFeatureEnabled } = useFeature(FeatureNames.Contact);
  const { enabled: quoteFeatureEnabled } = useFeature(FeatureNames.Quote);
  const { enabled: analyticFeatureEnabled } = useFeature(FeatureNames.Analytics);
  const {
    state: { stripeApiKey }
  } = useAppContext();
  const googleMapKey = mapFeature?.GoogleMapsApiKey || '';

  const [isLoadStripe, setIsLoadStripe] = React.useState<boolean>(false);

  const stripePromise = React.useMemo(() => {
    if (stripeApiKey && !isLoadStripe) {
      setIsLoadStripe(true);
      return loadStripe(stripeApiKey);
    }
    return null;
  }, [stripeApiKey]);

  let routes = [...LeadRoutes, ...ProductRoutes, ...QuestionnaireRoutes, ...SettingRoutes];
  if (contactFeatureEnabled) {
    routes = [...routes, ...ContactRoutes];
  }
  if (quoteFeatureEnabled) {
    routes = [...routes, ...QuoteRoutes];
  }
  if (analyticFeatureEnabled) {
    routes = [...routes, ...AnalyticRoutes];
  }
  const mainRoutes: RouteList = flattenRoutes(routes);

  return (
    <Elements stripe={stripePromise}>
      <MapProvider googleMapKey={googleMapKey}>
        <BrowserRouter>
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <MainLayoutProvider>
              <Routes>
                <Route path='/signout-redirect' element={<SignedOutPage name='Client Portal' redirectPath='/' />} />
                <Route path='/' element={<Navigate to='/leads' replace />} />
                {mainRoutes
                  .filter(({ component }) => component)
                  .map(({ path, component: Component }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <PrivateRoute permission={PermissionClientAdminRead}>
                          <ErrorBoundary>
                            <MainLayout>
                              <React.Suspense fallback={<Spinner />}>
                                <Component />
                              </React.Suspense>
                            </MainLayout>
                          </ErrorBoundary>
                        </PrivateRoute>
                      }
                    />
                  ))}
                {publicRoutes
                  .filter((r) => r.component)
                  .map((r) => (
                    <Route
                      element={
                        <PublicLayout>
                          <React.Suspense fallback={<Spinner />}>
                            <r.component />
                          </React.Suspense>
                        </PublicLayout>
                      }
                      path={r.path}
                      key={r.path}
                    />
                  ))}
                {fullPageRoutes
                  .filter(({ component }) => component)
                  .map(({ path, component: Component }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <PrivateRoute permission={PermissionClientAdminRead}>
                          <React.Suspense fallback={<Spinner />}>
                            <Component />
                          </React.Suspense>
                        </PrivateRoute>
                      }
                    />
                  ))}
              </Routes>
            </MainLayoutProvider>
          </QueryParamProvider>
        </BrowserRouter>
      </MapProvider>
    </Elements>
  );
};
